/**
 * System Status Bar — Slim HUD strip at top of all mobile pages.
 * Shows system status, TSB (freshness), and risk level in monospace.
 *
 * TASK-021: Now reads canonical TSB from useCanonicalTSB (same source
 * as Today page, Insights page, and Coach Live Vitals sidebar).
 * Sign convention: positive = fresh, negative = fatigued.
 */
import { cn } from '@/lib/utils';
import { useCanonicalTSB } from '@/hooks/useCanonicalTSB';
import { useDashboardData } from '@/hooks/useDashboardData';

function getRiskFromDecision(data: Record<string, unknown> | null | undefined): string {
  if (!data) return '—';

  const decision = typeof data.decision === 'string' ? data.decision.toUpperCase() : '';
  if (decision === 'REST' || decision === 'CANCEL') return 'HIGH';
  if (decision === 'MODIFY') return 'MED';
  if (decision) return 'LOW';

  const rec = typeof data.recommendation === 'string' ? data.recommendation.toLowerCase() : '';
  if (rec.includes('rest') || rec.includes('cancel')) return 'HIGH';
  if (rec.includes('modif') || rec.includes('caution')) return 'MED';
  if (rec) return 'LOW';

  return '—';
}

export function SystemStatusBar() {
  const { data: tsbData, isLoading: tsbLoading } = useCanonicalTSB();
  const { todayIntelligence, todayIntelligenceLoading } = useDashboardData();

  const tsb = tsbData.tsb;
  const tsbLabel = tsbLoading ? '...' : `${tsb > 0 ? '+' : ''}${Math.round(tsb)}`;
  const tsbColor = tsb > 5
    ? 'text-green-400'
    : tsb > -10
      ? 'text-amber-400'
      : 'text-red-400';

  const risk = getRiskFromDecision(todayIntelligence as Record<string, unknown> | null);
  const riskColor = risk === 'LOW' ? 'text-green-400' : risk === 'MED' ? 'text-amber-400' : risk === 'HIGH' ? 'text-red-400' : 'text-slate-500';

  return (
    <div className="lg:hidden fixed top-[calc(3.5rem+env(safe-area-inset-top,0px))] left-0 right-0 z-40 h-6 flex items-center justify-center gap-4 border-b border-white/[0.04] bg-[#0B0E14]/95 backdrop-blur-md">
      <span className="flex items-center gap-1.5">
        <span className="telemetry-dot telemetry-dot--green" style={{ width: 4, height: 4 }} />
        <span className="text-[8px] font-mono tracking-[0.12em] uppercase text-slate-500">
          SYS: ACTIVE
        </span>
      </span>
      <span className="w-px h-3 bg-white/[0.08]" />
      <span className={cn('text-[8px] font-mono tracking-[0.12em] uppercase', tsbLoading ? 'text-slate-400' : tsbColor)}>
        TSB: {tsbLabel}
      </span>
      <span className="w-px h-3 bg-white/[0.08]" />
      <span className={cn('text-[8px] font-mono tracking-[0.12em] uppercase', riskColor)}>
        RISK: {todayIntelligenceLoading ? '...' : risk}
      </span>
    </div>
  );
}
